/**
 * Centralized bar-fetch helper with 1m fallback for 0-bar instruments.
 *
 * Design (CEO [A] · DDD #180 / beta-readiness):
 *   Dollar bars are intentionally generated only for BTC/ETH/SOL. When a
 *   symbol's dollar (default) bar query returns [], we transparently retry
 *   with '1m'. This lets the 10 new instruments render immediately while the
 *   dollar-bar pipeline is not yet configured for them.
 *
 *   Auto-upgrade: once dollar bars exist (threshold configured), the first
 *   non-empty dollar result takes precedence and the fallback never fires.
 *
 * Scope:
 *   - Only the DEFAULT timeframe triggers fallback (dollar or undefined).
 *   - Explicit user selections (1H, 4H, 1D, etc.) are respected as-is.
 *   - BTC/ETH/SOL behavior is unchanged (dollar result is non-empty → no retry).
 */

import { gatewayApi } from './api';
import type { GatewayBarData } from '../types/gateway';

/** Canonical default timeframe. Matches useTerminalStore initial selectedTimeframe. */
export const DEFAULT_TIMEFRAME = 'dollar';

export interface FetchBarsResult {
  bars: GatewayBarData[];
  /** Timeframe actually used to retrieve the bars. */
  timeframeUsed: string;
  /**
   * True when the requested timeframe had 0 bars AND we fell back to '1m'.
   * Always false for non-default timeframes (user chose explicitly).
   */
  isFallback: boolean;
}

/**
 * Fetch bars with automatic 1m fallback for the default timeframe.
 *
 * @param symbol   Instrument symbol (e.g. 'XRPUSDT')
 * @param timeframe Requested timeframe; undefined means server default = dollar.
 * @param limit    Max bars to request.
 */
export async function fetchBarsWithFallback(
  symbol: string | undefined,
  timeframe: string | undefined,
  limit: number,
): Promise<FetchBarsResult> {
  const isDefaultTimeframe =
    timeframe === undefined || timeframe === DEFAULT_TIMEFRAME;

  const bars = await gatewayApi.bars(symbol, timeframe, limit);

  if (bars.length === 0 && isDefaultTimeframe) {
    // Retry with 1m. If that is also empty — or the retry itself throws — fall
    // through to the empty result so the chart shows chart-awaiting-bars, rather
    // than letting a 1m-fetch error reject the whole call (the default fetch
    // already succeeded with an empty array).
    try {
      const fallbackBars = await gatewayApi.bars(symbol, '1m', limit);
      if (fallbackBars.length > 0) {
        return { bars: fallbackBars, timeframeUsed: '1m', isFallback: true };
      }
    } catch {
      // 1m fallback retry failed — degrade gracefully to the empty default result.
    }
    // Both dollar and 1m empty (or 1m retry failed) — no data.
    return { bars: [], timeframeUsed: timeframe ?? DEFAULT_TIMEFRAME, isFallback: false };
  }

  return {
    bars,
    timeframeUsed: timeframe ?? DEFAULT_TIMEFRAME,
    isFallback: false,
  };
}
